/* eslint-disable max-lines */
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import cn from 'clsx'
import { useTranslation } from 'react-i18next';
import { DownloadSimpleIcon } from '@fronton/icons-react';
import {
  Button,
  ButtonWithGAClickButton,
  DEFAULT_IMAGE_TWO_COLUMNS_TRANSFORMATIONS,
  GalleryGrid,
  GalleryModal,
  LinkWithGAClickButton,
  Picture,
  Section,
  SectionSubTitle,
  Typography,
} from 'common/components';
import { KAZAKHSTAN } from 'common/constants/region-codes';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import {
  STORAGE_CDN_PATH,
  STORAGE_DESIGN_SIGN_UP_HREF,
  STORAGE_ELEMENT_BLOCK,
} from '../../storage-constants';
import {
  GalleryImageWrapper,
  galleryImage,
  galleryImageName,
  storageGalleryButtonWrapper,
  storageGalleryGrid,
  storageGalleryGridWithCatalogLink,
  storageGalleryGridWithoutCatalogLink,
  storageGalleryManual,
  storageGalleryManualLogo,
  storageGalleryManualText,
  storageGalleryManualTextLogo,
  storageGalleryManualTextWrapper,
} from './storage-gallery-section-styles';
import {
  IMAGE_PER_PAGE,
  INITIAL_IMAGE_PER_PAGE,
  getStorageGalleryList,
  StorageGalleryStructure,
} from './storage-gallery-section-constants';

type StorageGallerySectionProps = {
  regionId: string;
};

export const StorageGallerySection = memo(({ regionId }: StorageGallerySectionProps) => {
  const { t } = useTranslation();
  const bannerRefs = useRef<(HTMLAnchorElement | HTMLDivElement)[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [imagePerPage, setImagePerPage] = useState<number>(INITIAL_IMAGE_PER_PAGE);
  const [lastRefIndex, setLastRefIndex] = useState<number>(0);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const isRuRegion = regionId !== KAZAKHSTAN;
  const storageGalleryList = useMemo(() => getStorageGalleryList(), []);

  const imageList = useMemo(() => {
    const images = storageGalleryList.slice(0, imagePerPage);
    if (isRuRegion) {
      return images;
    }
    
    return images.filter((image): image is StorageGalleryStructure => Boolean(image));
  }, [imagePerPage, isRuRegion, storageGalleryList]);
  const modalImageList = useMemo(
    () => storageGalleryList.filter((image): image is StorageGalleryStructure => Boolean(image)),
    [storageGalleryList]
  );
  const isShowMoreBtnVisible = useMemo(() => imagePerPage < storageGalleryList.length, [imagePerPage, storageGalleryList]);

  const promotions = useMemo(
    () =>
      imageList.map((image, index: number) => ({
        name: `${STORAGE_ELEMENT_BLOCK}_middle`,
        creative: image?.src.slice(image?.src.lastIndexOf('/') + 1) || 'Скачать каталог хранения',
        position: (index + 1).toString(),
      })),
    [imageList]
  );

  const imagePromotions = useMemo(
    () =>
      modalImageList.map(image => ({
        name: `${STORAGE_ELEMENT_BLOCK}_middle`,
        creative: image?.src.slice(image?.src.lastIndexOf('/') + 1) || 'Скачать каталог хранения',
        position: (imageList.indexOf(image) + 1).toString(),
      })),
    [modalImageList, imageList]
  );

  usePromoViewEvent(bannerRefs, `${STORAGE_ELEMENT_BLOCK} / gallery`, promotions, lastRefIndex);

  const handleBannerClick = useCallback(
    (index: number) => () => {
      analyticsUtils.promoClickEvent(`${STORAGE_ELEMENT_BLOCK} / gallery`, imagePromotions[index]);
    },
    [imagePromotions]
  );

  const handleImageClick = useCallback(
    (index: number) => () => {
      setIsModalOpen(true);
      setActiveImageIndex(index);
      handleBannerClick(index)();
    },
    [setActiveImageIndex, handleBannerClick]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, [setIsModalOpen]);

  const handleClickMoreBtn = useCallback(() => {
    setLastRefIndex(imagePerPage);
    setImagePerPage(imagePerPage + IMAGE_PER_PAGE);
    analyticsUtils.clickButtonEvent(STORAGE_ELEMENT_BLOCK, t('gallery-section.button'), t('gallery-section.element-text'));
  }, [imagePerPage, setLastRefIndex, setImagePerPage, t]);

  return (
    <Section>
      <SectionSubTitle margin="m">{t('gallery-section.title')}</SectionSubTitle>
      <GalleryGrid
        className={cn(
          storageGalleryGrid,
          { 
            [storageGalleryGridWithCatalogLink]: isRuRegion,
            [storageGalleryGridWithoutCatalogLink]: !isRuRegion,
           }
        )}
      >
        {imageList.map((image: StorageGalleryStructure | null, index: number) =>
          (image) ? (
            <GalleryImageWrapper
              ref={(el: HTMLDivElement | null) => {
                if (el && bannerRefs) bannerRefs.current[index] = el;
              }}
              className={image.className}
              data-position={index}
              key={createElementKey('storage-gallery-image', index)}
              onClick={handleImageClick(modalImageList.indexOf(image))}>
              <Picture
                alt={image.alt}
                className={galleryImage}
                src={image.srcSmall}
                title={image.title}
                transformations={image.transformations || DEFAULT_IMAGE_TWO_COLUMNS_TRANSFORMATIONS}
              />
              <div className={galleryImageName}>
                <Typography color="text-invert" variant="m" size="body_accent">
                  {image.alt}
                </Typography>
              </div>
            </GalleryImageWrapper>
          ) : (
            // only for ru locale
            <LinkWithGAClickButton
              elementBlock={STORAGE_ELEMENT_BLOCK}
              elementName="Скачать каталог хранения"
              elementText={t('gallery-section.title')}
              className={storageGalleryManual}
              href="https://storage.yandexcloud.net/leroymerlin-ru-landings-docs/storage/Leroy_Merlin_Storage_Catalog.pdf"
              key={createElementKey('storage-gallery-image', index)}
              rel="noreferrer"
              target="_blank"
              title="Скачать каталог хранения">
              <div className={storageGalleryManualTextWrapper}>
                <DownloadSimpleIcon
                  className={storageGalleryManualTextLogo}
                  type="outline"
                  size="l"
                  color="text-primary"
                />
                <Typography className={storageGalleryManualText} variant="l" size="subtitle">
                  Скачать каталог хранения
                </Typography>
              </div>
              <img
                className={storageGalleryManualLogo}
                alt="Скачать каталог хранения"
                src={`${STORAGE_CDN_PATH}/gallery/document-icon-new.png/document-icon-new.png`}
              />
            </LinkWithGAClickButton>
          )
        )}
      </GalleryGrid>
      {isShowMoreBtnVisible && (
        <div className={storageGalleryButtonWrapper}>
          <Button onClick={handleClickMoreBtn} rounded>
            {t('gallery-section.button')}
          </Button>
        </div>
      )}

      <GalleryModal
        clickedImageIndex={activeImageIndex}
        elementBlock={STORAGE_ELEMENT_BLOCK}
        elementText={`${t('gallery-section.element-text')} / modal`}
        galleryList={modalImageList}
        onClose={handleCloseModal}
        renderFooterButton={() => (
          <ButtonWithGAClickButton
            aria-label={t('gallery-section.modal-button') || ''}
            elementBlock={STORAGE_ELEMENT_BLOCK}
            elementName={t('gallery-section.modal-button')}
            elementText={`${t('gallery-section.element-text')} / modal`}
            href={`${STORAGE_DESIGN_SIGN_UP_HREF}${regionId}`}
            invert
            onClick={handleCloseModal}
            target="_blank"
            title={t('gallery-section.modal-button') || ''}
            variant="accent">
            {t('gallery-section.modal-button')}
          </ButtonWithGAClickButton>
        )}
        show={isModalOpen}
        isShowImageName
      />
    </Section>
  );
});
